import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ViewListIcon from "@mui/icons-material/ViewList";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import KitchenIcon from "@mui/icons-material/Kitchen";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import apiClient from "utils/apiClient";
import { ENDPOINTS } from "utils/endpoints";
import { RED, RED_LIGHT, BORDER } from "assets/theme/brandColors";
import { useUnitSystem } from "context/UnitSystemContext";
import UnitToggle from "components/UnitToggle";
import { fmtIng, fmtPantry, scaleIng } from "utils/ingredientFormatting";
import { CATALOG_DEFAULT_SERVINGS } from "utils/servings";
import {
  CATEGORIES,
  OTHER_KEY,
  aggregateWithPantry,
  groupByCategory,
  canonical,
  ingredientKey,
} from "utils/ingredientCategories";
import { buildGroceryListText } from "utils/groceryText";
import { copyText } from "utils/clipboard";

function ServingsControl({ servings, onChange }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Box
        component="button"
        onClick={() => onChange(Math.max(1, servings - 1))}
        disabled={servings <= 1}
        sx={{
          width: 22,
          height: 22,
          borderRadius: "5px",
          border: "1.5px solid",
          borderColor: servings <= 1 ? "#d4d0c8" : "#b8b4aa",
          bgcolor: "#faf9f7",
          color: servings <= 1 ? "#b8b4aa" : "#444",
          fontSize: 14,
          lineHeight: 1,
          cursor: servings <= 1 ? "default" : "pointer",
          fontFamily: "Roboto, sans-serif",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          "&:hover": servings > 1 ? { borderColor: "#a01c27", color: "#a01c27" } : {},
        }}
      >
        −
      </Box>
      <Typography
        sx={{ fontSize: 12, fontWeight: 600, color: "#222", minWidth: 72, textAlign: "center" }}
      >
        {servings} serving{servings !== 1 ? "s" : ""}
      </Typography>
      <Box
        component="button"
        onClick={() => onChange(servings + 1)}
        sx={{
          width: 22,
          height: 22,
          borderRadius: "5px",
          border: "1.5px solid",
          borderColor: "#b8b4aa",
          bgcolor: "#faf9f7",
          color: "#444",
          fontSize: 14,
          lineHeight: 1,
          cursor: "pointer",
          fontFamily: "Roboto, sans-serif",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          "&:hover": { borderColor: "#a01c27", color: "#a01c27" },
        }}
      >
        +
      </Box>
    </Box>
  );
}

ServingsControl.propTypes = {
  servings: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

function IngredientItem({ label, badge, cardBg, cardBorder, checked, onToggle }) {
  return (
    <Box
      onClick={onToggle}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        p: "5px 10px",
        bgcolor: checked ? "#f0f2f5" : cardBg || "#fff",
        border: "1px solid",
        borderColor: checked ? "#b8bcc8" : cardBorder || BORDER,
        borderRadius: 1.5,
        cursor: "pointer",
        userSelect: "none",
        transition: "all 0.15s",
      }}
    >
      <Checkbox
        checked={checked}
        size="small"
        disableRipple
        sx={{
          p: 0,
          color: "#a09890",
          "&.Mui-checked": { color: RED },
          pointerEvents: "none",
        }}
      />
      <Typography
        variant="body2"
        sx={{
          fontSize: 12,
          color: checked ? "#999" : "#2a2a2a",
          textDecoration: checked ? "line-through" : "none",
          flex: 1,
          lineHeight: 1.3,
          fontSize: 13,
        }}
      >
        {label}
      </Typography>
      {badge && (
        <Box
          sx={{
            bgcolor: RED_LIGHT,
            color: RED,
            fontSize: 10,
            fontWeight: 700,
            px: 0.6,
            py: 0.1,
            borderRadius: 10,
            flexShrink: 0,
          }}
        >
          ×{badge}
        </Box>
      )}
    </Box>
  );
}

IngredientItem.propTypes = {
  label: PropTypes.string.isRequired,
  badge: PropTypes.number,
  cardBg: PropTypes.string,
  cardBorder: PropTypes.string,
  checked: PropTypes.bool,
  onToggle: PropTypes.func,
};

function IngredientGrid({ items, cardBg, cardBorder, checkedKeys, onToggle }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
        gap: 0.75,
      }}
    >
      {items.map((item, i) => (
        // React key stays index-based: two lines in one grid can legitimately
        // share an itemKey (same ingredient listed in two units), and they are
        // meant to tick together rather than trigger a duplicate-key warning.
        <IngredientItem
          key={i}
          label={item.label}
          badge={item.badge}
          cardBg={cardBg}
          cardBorder={cardBorder}
          checked={checkedKeys ? checkedKeys.has(item.itemKey) : false}
          onToggle={onToggle ? () => onToggle(item.itemKey) : undefined}
        />
      ))}
    </Box>
  );
}

IngredientGrid.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      badge: PropTypes.number,
      itemKey: PropTypes.string,
    })
  ).isRequired,
  cardBg: PropTypes.string,
  cardBorder: PropTypes.string,
  checkedKeys: PropTypes.instanceOf(Set),
  onToggle: PropTypes.func,
};

function CategorySection({ catKey, items, checkedKeys, onToggle }) {
  const [open, setOpen] = useState(true);
  const meta = CATEGORIES.find((c) => c.key === catKey);
  const emoji = meta ? meta.emoji : "🛒";
  const label = meta ? meta.label : "Other";
  const bg = meta?.color || "#ebebeb";
  const border = meta?.border || "#b0b0b0";

  return (
    <Box
      sx={{
        mb: 1.5,
        borderRadius: 2,
        border: `1.5px solid ${border}`,
        bgcolor: bg,
        overflow: "hidden",
      }}
    >
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "pointer",
          userSelect: "none",
          px: 1.5,
          py: 1,
        }}
      >
        <Typography sx={{ fontSize: 15, lineHeight: 1 }}>{emoji}</Typography>
        <Typography
          sx={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.5, color: "#222", flex: 1 }}
        >
          {label}
        </Typography>
        <Typography sx={{ fontSize: 11, color: "#666", mr: 0.5 }}>{items.length}</Typography>
        <ExpandMoreIcon
          sx={{
            fontSize: 16,
            color: "#888",
            transition: "transform 0.2s",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          <IngredientGrid
            items={items}
            cardBg="#fff"
            cardBorder={border}
            checkedKeys={checkedKeys}
            onToggle={onToggle}
          />
        </Box>
      </Collapse>
    </Box>
  );
}

CategorySection.propTypes = {
  catKey: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired,
  checkedKeys: PropTypes.instanceOf(Set),
  onToggle: PropTypes.func,
};

function SectionTitle({ children }) {
  return (
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 1,
        textTransform: "uppercase",
        color: RED,
        mb: 1.5,
        pb: 0.75,
        borderBottom: "2px solid #e8c8cb",
      }}
    >
      {children}
    </Typography>
  );
}

SectionTitle.propTypes = {
  children: PropTypes.node.isRequired,
};

function PantrySection({ items, metric }) {
  if (!items.length) return null;
  return (
    <Box
      sx={{
        bgcolor: "#f5ecc8",
        border: "1.5px solid #c8a030",
        borderRadius: 2,
        p: 2,
        mt: 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
        <KitchenIcon sx={{ fontSize: 15, color: "#7a5c00" }} />
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 1,
            color: "#7a5c00",
          }}
        >
          Cupboard — you probably have these
        </Typography>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
          gap: 1,
        }}
      >
        {items.map((item, i) => (
          <IngredientItem key={i} label={fmtPantry(item, metric) || item.name} />
        ))}
      </Box>
    </Box>
  );
}

PantrySection.propTypes = {
  items: PropTypes.array.isRequired,
  metric: PropTypes.bool,
};

function UnconfirmedPantrySection({ items, onConfirm, busyIds, metric }) {
  if (!items.length) return null;
  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1.25,
          "&::before, &::after": {
            content: '""',
            flex: 1,
            height: "1px",
            bgcolor: "#d0ccc4",
          },
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 1,
            color: "#6e6e6e",
            whiteSpace: "nowrap",
          }}
        >
          Not in your cupboard yet
        </Typography>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
          gap: 1,
        }}
      >
        {items.map((item) => (
          <Box
            key={item.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: "8px 12px",
              bgcolor: "#faf9f7",
              border: "1.5px solid #b89030",
              borderRadius: 2,
              transition: "border-color 0.15s, opacity 0.2s",
              opacity: busyIds.has(item.id) ? 0.5 : 1,
              "&:hover": { borderColor: "#7a5c00" },
            }}
          >
            <Typography variant="body2" sx={{ fontSize: 13, color: "#2a2a2a", flex: 1 }}>
              {fmtPantry(item, metric) || item.name}
            </Typography>
            <Tooltip title="I have this">
              <span>
                <IconButton
                  size="small"
                  disabled={busyIds.has(item.id)}
                  onClick={() => onConfirm(item)}
                  sx={{ p: 0, color: "#9a7800", "&:hover": { color: "#7a5c00" } }}
                >
                  <AddCircleOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

UnconfirmedPantrySection.propTypes = {
  items: PropTypes.array.isRequired,
  onConfirm: PropTypes.func.isRequired,
  busyIds: PropTypes.instanceOf(Set).isRequired,
  metric: PropTypes.bool,
};

function Ingredients({
  programId,
  programTitle,
  servingsMap,
  onServingsChange,
  onSelectRecipe,
  refreshKey,
}) {
  // `loaded` matters here: tick-off state is keyed by the formatted label, which
  // embeds the unit. Rendering before the user's unit preference arrives shows
  // US labels that match nothing in the restored set, so ticks visibly drop off
  // and then reappear a moment later. Waiting avoids that flash.
  const { metric, setMetric, loaded: unitsLoaded } = useUnitSystem();
  const [view, setView] = useState("aggregated");
  const [copyMsg, setCopyMsg] = useState("");
  const [activeIngredients, setActiveIngredients] = useState([]);
  // Raw per-recording pantry increments, not yet summed per item: the servings
  // scale is per *recipe*, so the rows have to be scaled before they collapse
  // into one row per pantry item, and servings can change after the fetch.
  const [pantryIncrements, setPantryIncrements] = useState([]);
  const [userPantryIds, setUserPantryIds] = useState(new Set());
  const [busyIds, setBusyIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  // defaultServingsMap: { recipeId -> number } — the recipe's canonical serving count from API
  const [defaultServingsMap, setDefaultServingsMap] = useState({});

  // Tick-off state is keyed by ingredientKey(), not by the formatted label:
  // the label embeds quantity and unit, so switching units or changing
  // servings used to silently drop every tick. The `_v2` suffix retires the
  // old label-keyed entries — they cannot be migrated (the units they were
  // written in are unknowable), so they are left to expire rather than
  // half-matched against the new keys.
  const _storageKey = programId ? `grocery_checked_v2_${programId}` : null;
  const [checkedKeys, setCheckedKeys] = useState(() => {
    if (!programId) return new Set();
    try {
      const raw = localStorage.getItem(`grocery_checked_v2_${programId}`);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  });

  const toggleChecked = (key) => {
    setCheckedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      if (_storageKey) {
        try {
          localStorage.setItem(_storageKey, JSON.stringify([...next]));
        } catch {}
      }
      return next;
    });
  };

  useEffect(() => {
    if (!programId) return;
    (async () => {
      try {
        const [res, myPantryRes] = await Promise.all([
          apiClient.get(ENDPOINTS.schedule.programActiveIngredients(programId)),
          apiClient.get(ENDPOINTS.pantry.myItems),
        ]);
        let ingredients = res.data.active_ingredients || [];
        const pantry = res.data.pantry_items || [];

        // If all recipes are completed, active_program_ingredients returns nothing.
        // Fall back to the full ingredient list so past schedules still show.
        if (ingredients.length === 0) {
          const fullRes = await apiClient.get(ENDPOINTS.schedule.programIngredients(programId));
          ingredients = Array.isArray(fullRes.data) ? fullRes.data : [];
        }

        // Record each recipe's default serving count from the API
        const defaults = {};
        ingredients.forEach((r) => {
          defaults[String(r.recipe_id)] = r.servings || CATALOG_DEFAULT_SERVINGS;
        });
        setDefaultServingsMap(defaults);
        setActiveIngredients(ingredients);
        setPantryIncrements(pantry);
        setUserPantryIds(new Set((myPantryRes.data || []).map((p) => p.id)));
      } catch (err) {
        console.error("Failed to fetch ingredients:", err.message);
      } finally {
        setLoading(false);
      }
    })();
    // `refreshKey` changes when the schedule itself does (a recipe replaced or
    // removed): the totals have to come from the new set of recipes, so the
    // fetch re-runs and everything downstream — scaling, aggregateWithPantry,
    // the pantry fold — re-derives exactly as it does for a servings change.
  }, [programId, refreshKey]);

  const handleConfirmPantry = async (item) => {
    if (busyIds.has(item.id)) return;
    setBusyIds((prev) => new Set(prev).add(item.id));
    try {
      await apiClient.post(ENDPOINTS.pantry.myItems, { pantry_item_id: item.id });
      setUserPantryIds((prev) => new Set(prev).add(item.id));
    } catch (err) {
      console.error("Failed to add pantry item:", err.message);
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  // The scale factor for one recipe, given the user's serving choice.
  const scaleFor = (recipeId) => {
    const id = String(recipeId);
    const def = defaultServingsMap[id] || CATALOG_DEFAULT_SERVINGS;
    const cur = (servingsMap && servingsMap[id]) ?? def;
    return cur / def;
  };

  // Apply per-recipe serving scaling before aggregation
  const scaledIngredients = activeIngredients.map((recipe) => ({
    ...recipe,
    ingredients: (recipe.ingredients || []).map((ing) => scaleIng(ing, scaleFor(recipe.recipe_id))),
  }));

  // Pantry increments collapse to one row per pantry item — several recipes,
  // and several steps within a recipe, can each contribute to the same item.
  const collapsePantry = (rows) => {
    const map = {};
    rows.forEach((p) => {
      if (!map[p.id]) {
        map[p.id] = { ...p, quantity: Number(p.quantity) || 0 };
      } else {
        map[p.id].quantity += Number(p.quantity) || 0;
        if (map[p.id].quantity_metric != null && p.quantity_metric != null) {
          map[p.id].quantity_metric = String(
            (Number(map[p.id].quantity_metric) || 0) + (Number(p.quantity_metric) || 0)
          );
        }
      }
    });
    return Object.values(map);
  };

  const pantryItems = collapsePantry(pantryIncrements);

  // Fold pantry-side contributions of anything the schedule is already buying
  // into the shopping total (see aggregateWithPantry) — otherwise an item
  // classified as an Ingredient in one recipe and a PantryItem in another is
  // counted once and left quietly short by the rest. Only active recipes
  // contribute, and each row is scaled by its own recipe's servings, exactly
  // as that recipe's ingredients are.
  const activeRecipeIds = new Set(activeIngredients.map((r) => String(r.recipe_id)));
  const activePantry = collapsePantry(
    pantryIncrements
      .filter((p) => activeRecipeIds.has(String(p.recipe_id)))
      .map((p) => scaleIng(p, scaleFor(p.recipe_id)))
  );
  const { items: aggregated, foldedPantryIds } = aggregateWithPantry(
    scaledIngredients,
    activePantry
  );

  // Folded rows are now part of the shopping line, so they must not also show
  // as cupboard staples — nor be subtracted back out below.
  const shownPantry = pantryItems.filter((p) => !foldedPantryIds.has(p.id));
  const confirmedPantry = shownPantry.filter((p) => userPantryIds.has(p.id));
  const unconfirmedPantry = shownPantry.filter((p) => !userPantryIds.has(p.id));

  // Build a lookup of confirmed pantry quantities (keyed by canonical ingredient name).
  const pantryLookup = {};
  confirmedPantry.forEach((item) => {
    const key = canonical(item.name || "");
    const itemUnit = (item.unit || "").toLowerCase();
    if (!pantryLookup[key]) {
      pantryLookup[key] = { quantity: Number(item.quantity) || 0, unit: itemUnit };
    } else if (pantryLookup[key].unit === itemUnit) {
      pantryLookup[key].quantity += Number(item.quantity) || 0;
    }
  });

  // Subtract confirmed pantry stock from the aggregated shopping list.
  const netAggregated = aggregated
    .map((agg) => {
      const key = canonical(agg.ingredient_name || "");
      const sub = pantryLookup[key];
      if (!sub || sub.quantity <= 0) return agg;
      const aggUnit = (agg.unit || "").toLowerCase();
      if (aggUnit === sub.unit) {
        const net = (agg.quantity || 0) - sub.quantity;
        if (net <= 0) return null;
        // Subtracting pantry stock invalidates the pre-subtraction 1/24ths
        // total (see aggregate() in ingredientCategories.js) — drop it so
        // fmtIng falls back to rounding the net decimal instead of
        // displaying a total that doesn't reflect the subtraction.
        return { ...agg, quantity: net, rawQuantity: null, frac24: undefined };
      }
      console.warn(
        `[pantry] unit mismatch for "${agg.ingredient_name}": pantry="${sub.unit}" vs shopping="${aggUnit}"`
      );
      return agg;
    })
    .filter(Boolean);

  const categoryGroups = groupByCategory(netAggregated);
  const orderedCatKeys = [
    ...CATEGORIES.map((c) => c.key).filter((k) => categoryGroups[k]),
    ...(categoryGroups[OTHER_KEY] ? [OTHER_KEY] : []),
  ];

  // Average servings across all recipes (for the summary chip)
  const recipeIds = activeIngredients.map((r) => String(r.recipe_id));
  const avgServings =
    recipeIds.length > 0
      ? Math.round(
          recipeIds.reduce((s, id) => {
            const def = defaultServingsMap[id] || CATALOG_DEFAULT_SERVINGS;
            const cur = (servingsMap && servingsMap[id]) ?? def;
            return s + cur;
          }, 0) / recipeIds.length
        )
      : null;

  // Copy as plain text, using the same category headings shown on screen and
  // skipping anything already ticked off.
  const handleCopyList = async () => {
    const sections = orderedCatKeys.map((key) => {
      const meta = CATEGORIES.find((c) => c.key === key);
      return {
        emoji: meta ? meta.emoji : "🛒",
        label: meta ? meta.label : "Other",
        items: (categoryGroups[key] || [])
          .filter((ing) => !checkedKeys.has(ingredientKey(ing.ingredient_name, ing.unit)))
          .map((ing) => fmtIng(ing, metric))
          .filter(Boolean),
      };
    });

    // Cupboard staples have no tick control of their own, so nothing to filter.
    const cupboardItems = unconfirmedPantry
      .map((item) => fmtPantry(item, metric) || item.name)
      .filter(Boolean);

    const text = buildGroceryListText({
      title: programTitle,
      avgServings,
      sections,
      cupboardItems,
    });

    if (!text) {
      setCopyMsg("Everything is ticked off — nothing to copy.");
      return;
    }

    const ok = await copyText(text);
    setCopyMsg(ok ? "Grocery list copied." : "Couldn't copy — your browser blocked it.");
  };

  if (loading || !unitsLoaded) {
    return (
      <Typography variant="body2" color="secondary" sx={{ py: 4, textAlign: "center" }}>
        Loading grocery list…
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(_, v) => v && setView(v)}
          size="small"
          sx={{
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontSize: 13,
              fontWeight: 500,
              px: 2,
              py: 0.75,
              border: "1.5px solid #c8c4bc",
              color: "#555",
            },
            "& .MuiToggleButton-root.Mui-selected": {
              bgcolor: RED,
              color: "#fff",
              borderColor: RED,
              "&:hover": { bgcolor: "#7d1520" },
            },
          }}
        >
          <ToggleButton value="aggregated">
            <ViewListIcon sx={{ fontSize: 16, mr: 0.75 }} />
            Groceries
          </ToggleButton>
          <ToggleButton value="byrecipe">
            <MenuBookIcon sx={{ fontSize: 16, mr: 0.75 }} />
            By recipe
          </ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Tooltip title="Copy the remaining items as text you can paste into a message">
            <Button
              onClick={handleCopyList}
              size="small"
              startIcon={<ContentCopyIcon sx={{ fontSize: 15 }} />}
              sx={{
                textTransform: "none",
                fontSize: 13,
                fontWeight: 600,
                color: RED,
                border: "1.5px solid",
                borderColor: "#c8c4bc",
                px: 1.5,
                py: 0.6,
                "&:hover": { bgcolor: RED_LIGHT, borderColor: RED },
              }}
            >
              Copy list
            </Button>
          </Tooltip>
          <UnitToggle metric={metric} onChange={() => setMetric((v) => !v)} />
        </Box>
      </Box>

      {avgServings !== null && view === "aggregated" && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2.5,
            px: 2,
            py: 1.25,
            bgcolor: "#fff",
            borderRadius: 2,
            border: "1px solid #c8ccd4",
          }}
        >
          <Typography sx={{ fontSize: 13, color: "#555", flex: 1 }}>
            Quantities shown for an average of{" "}
            <Box component="span" sx={{ fontWeight: 700, color: "#333" }}>
              {avgServings} serving{avgServings !== 1 ? "s" : ""}
            </Box>{" "}
            per recipe. Adjust per recipe in the{" "}
            <Box
              component="span"
              sx={{
                color: RED,
                fontWeight: 600,
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={() => setView("byrecipe")}
            >
              By recipe
            </Box>{" "}
            view.
          </Typography>
        </Box>
      )}

      {view === "aggregated" && (
        <Box>
          {orderedCatKeys.map((key) => (
            <CategorySection
              key={key}
              catKey={key}
              items={(categoryGroups[key] || [])
                .map((ing) => ({
                  label: fmtIng(ing, metric),
                  itemKey: ingredientKey(ing.ingredient_name, ing.unit),
                }))
                .filter(({ label }) => label)}
              checkedKeys={checkedKeys}
              onToggle={toggleChecked}
            />
          ))}
          <Box sx={{ mt: 3 }}>
            <UnconfirmedPantrySection
              items={unconfirmedPantry}
              onConfirm={handleConfirmPantry}
              busyIds={busyIds}
              metric={metric}
            />
            <PantrySection items={confirmedPantry} metric={metric} />
          </Box>
        </Box>
      )}

      {view === "byrecipe" && (
        <Box>
          {activeIngredients.map((recipe, i) => {
            const id = String(recipe.recipe_id);
            const def = defaultServingsMap[id] || CATALOG_DEFAULT_SERVINGS;
            const cur = (servingsMap && servingsMap[id]) ?? def;
            const scale = cur / def;
            return (
              <Box
                key={i}
                sx={{
                  mb: 2.5,
                  borderRadius: 2,
                  bgcolor: "#fff",
                  overflow: "hidden",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                  borderLeft: `4px solid ${RED}`,
                  border: "1px solid #ddd",
                  borderLeftWidth: 4,
                  borderLeftColor: RED,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2.5,
                    pt: 2,
                    pb: 1.5,
                  }}
                >
                  <Typography
                    onClick={() => onSelectRecipe && onSelectRecipe(recipe.recipe_id)}
                    sx={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: onSelectRecipe ? RED : "#222",
                      letterSpacing: 0,
                      cursor: onSelectRecipe ? "pointer" : "default",
                      "&:hover": onSelectRecipe ? { textDecoration: "underline" } : {},
                    }}
                  >
                    {recipe.recipe_title}
                  </Typography>
                  <ServingsControl
                    servings={cur}
                    onChange={(next) => onServingsChange && onServingsChange(id, next)}
                  />
                </Box>
                <Box
                  sx={{
                    px: 2.5,
                    pb: 2,
                    borderTop: "1px solid #f0f0f0",
                    pt: 1.5,
                  }}
                >
                  <IngredientGrid
                    items={(recipe.ingredients || [])
                      .map((ing) => ({
                        label: fmtIng(scaleIng(ing, scale), metric),
                        // Scoped to the recipe so ticking an ingredient here
                        // marks it for this recipe only, matching how the
                        // by-recipe view already behaved.
                        itemKey: `${id}::${ingredientKey(ing.ingredient_name, ing.unit)}`,
                      }))
                      .filter(({ label }) => label)}
                    checkedKeys={checkedKeys}
                    onToggle={toggleChecked}
                  />
                </Box>
              </Box>
            );
          })}
          <UnconfirmedPantrySection
            items={unconfirmedPantry}
            onConfirm={handleConfirmPantry}
            busyIds={busyIds}
            metric={metric}
          />
          <PantrySection items={confirmedPantry} metric={metric} />
        </Box>
      )}

      <Snackbar
        open={!!copyMsg}
        autoHideDuration={3000}
        onClose={() => setCopyMsg("")}
        message={copyMsg}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}

Ingredients.propTypes = {
  programId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  programTitle: PropTypes.string,
  servingsMap: PropTypes.objectOf(PropTypes.number),
  onServingsChange: PropTypes.func,
  onSelectRecipe: PropTypes.func,
  // Changing this re-fetches the list. Bumped when the schedule's recipes
  // change, not when servings do — those re-derive from state already loaded.
  refreshKey: PropTypes.number,
};

export default Ingredients;
